import {
    Box,
    Button,
    Flex,
    Icon,
    IconButton,
    ListItem,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    Tooltip,
    UnorderedList,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import Papa from 'papaparse';
import { useCallback, useState } from 'react';
import { BiX } from 'react-icons/bi';

import { usePreloadResources } from '../api/trade';
import { FileInput } from '../widgets/FileInput';
import { NumberCell } from '../widgets/Table/NumberCell';
import { Table } from '../widgets/Table/Table';
import { TextCell } from '../widgets/Table/TextCell';
import { ManualTrade } from './TradeBlotter';
import { tradeCalculator } from './tradeCalculator';

export const CSVUpload = ({ addTrades }: { addTrades: (trades: ManualTrade[]) => void }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [trades, setTrades] = useState<ManualTrade[]>();
    const toast = useToast();
    const { mutate, isPending } = usePreloadResources();

    const close = useCallback(() => {
        if (isPending) {
            return;
        }
        setTrades([]);
        onClose();
    }, [isPending, onClose]);

    const onImport = useCallback(() => {
        if (!trades?.length) {
            return;
        }

        mutate(trades, {
            onSuccess: () => {
                trades?.length && addTrades(trades);
                close();
            },
        });
    }, [addTrades, close, mutate, trades]);

    const table = useReactTable({
        defaultColumn: {
            enableColumnFilter: false,
            enableSorting: false,
        },
        data: trades || [],
        columns: [
            {
                id: 'portfolioID',
                header: 'Portfolio Identifier',
                accessorFn: (trade) => trade.portfolio?.portfolioGuid || trade.portfolio?.pId || trade.portfolio?.aC,
                cell: ({ getValue }) => <TextCell value={getValue()} />,
            },
            {
                id: 'assetID',
                header: 'Asset Identifier',
                accessorFn: (trade) => trade.asset?.symbol || trade.asset?.cusip || trade.asset?.sId,
                cell: ({ getValue }) => <TextCell value={getValue()} />,
            },
            {
                id: 'side',
                header: 'Side',
                accessorKey: 'side',
                cell: ({ getValue }) => <TextCell value={getValue().toUpperCase()} />,
            },
            {
                id: 'quantity',
                header: 'Quantity',
                accessorKey: 'quantity',
                cell: ({ getValue }) => <NumberCell value={getValue()} />,
            },
            {
                id: 'orderType',
                header: 'Order Type',
                accessorKey: 'orderType',
                cell: ({ getValue }) => <TextCell value={getValue()} />,
            },
            {
                id: 'removeItem',
                header: '',
                cell: ({ row }) => {
                    return (
                        <IconButton
                            size="xs"
                            icon={<Icon as={BiX} />}
                            aria-label="remove trade"
                            colorScheme="red"
                            onClick={() => setTrades((current) => current?.filter((_, idx) => row.index !== idx))}
                            variant="outline"
                        />
                    );
                },
            },
        ],
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <>
            <Tooltip
                label={
                    <Box>
                        <Text>Expected columns</Text>
                        <UnorderedList pl={4}>
                            <ListItem>
                                portfolioGuid <b>OR</b> aC <b>OR</b> pId
                            </ListItem>
                            <ListItem>side [BUY/SELL]</ListItem>
                            <ListItem>
                                symbol <b>OR</b> cusip <b>OR</b> sId
                            </ListItem>
                            <ListItem>quantity</ListItem>
                            <ListItem>orderType [MKT/LMT]</ListItem>
                        </UnorderedList>
                    </Box>
                }
            >
                <Flex>
                    <FileInput
                        accept=".csv"
                        onChange={(ev) => {
                            const file = ev.target.files?.[0];
                            if (!file) {
                                return;
                            }
                            parseFile(file, (manualTrades) => {
                                if (!manualTrades.length) {
                                    return toast({
                                        title: 'Empty CSV file',
                                        description: 'No trades found in the selected file',
                                        status: 'warning',
                                        duration: 3000,
                                    });
                                }

                                setTrades(manualTrades);
                                onOpen();
                            });
                        }}
                    />
                </Flex>
            </Tooltip>

            <Modal isOpen={isOpen} onClose={onClose} size="5xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Review import</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Table table={table} />
                    </ModalBody>

                    <ModalFooter justifyContent="space-between">
                        <Button variant="ghost" onClick={onClose} isLoading={isPending}>
                            Cancel
                        </Button>
                        <Button colorScheme="teal" onClick={onImport} isLoading={isPending}>
                            Import
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

const parseFile = (file: File, cb: (trades: ManualTrade[]) => void) => {
    Papa.parse(file, {
        delimiter: ',',
        header: true,
        transformHeader: (header) => {
            return header.toLocaleLowerCase().replace(/_|\s/g, '');
        },
        complete: (results) => {
            const parsed = results.data.map((raw: any) => {
                const {
                    portfolioguid: portfolioGuid,
                    ac: aC,
                    pid: pId,

                    symbol,
                    cusip,
                    sid: sId,

                    side,
                    quantity,
                    ordertype: orderType,
                } = raw;

                return {
                    portfolio: {
                        portfolioGuid,
                        aC,
                        pId,
                    },
                    side: side.toUpperCase(),
                    asset: {
                        symbol,
                        cusip,
                        sId,
                    },
                    quantity,
                    orderType,
                    price: {
                        type:
                            orderType === 'MKT' ? 'MID' : tradeCalculator.getPriceTypeFromTradeSide(side.toUpperCase()),
                    },
                } as ManualTrade;
            });
            cb(parsed);
        },
        skipEmptyLines: true,
    });
};
